import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Typography, Flex, Select, Radio, Button, Table, Tag, Input, Tooltip, Space, DatePicker, Segmented, Empty, message, Spin } from "antd";
import { PlusOutlined, AppstoreOutlined, BarsOutlined, ThunderboltOutlined, PhoneOutlined, MailOutlined, StarFilled, HeartFilled, CopyOutlined, CheckOutlined, SendOutlined, CalculatorOutlined, CheckCircleOutlined, FullscreenOutlined, FormOutlined, CalendarOutlined } from "@ant-design/icons";

import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { getCookie } from "../../../utils/cookieUtils";
import { AVAILABLE_STATUSES, PRODUCT_REMARKS_CONFIG } from "./leadsData";
import LeadCardGrid from "./LeadCardGrid";
import LeadDetailDrawer from "./LeadDetailDrawer";
import AiSummaryModal from "./AiSummaryModal";
import AddLeadModal from "./AddLeadModal";
import CompanyFullViewModal from "./CompanyFullViewModal";
import QuotationModal from "../quotation/QuotationModal";
import ProposalModal from "../quick-access/ProposalModal";
import AddDealModal from "../pipeline/AddDealModal";
import MailComposeDrawer from "../mail/MailComposeDrawer";
import ScheduleActivityModal from "../activities/ScheduleActivityModal";
import { getAdminLeadCompanies, getEmployeeLeadCompanies, getCompanyFullDetails, getAdminLeadSets, getEmployeeLeadSets, updateLead, addLeadRemark, createLead, addBulkLeads } from "../../../services/leadService";
import { createDeal } from "../../../services/pipelineService";
import { getClients } from "../../../services/clientService";
import "./leadsTemplate.css";

const { Title, Text } = Typography;

const LEADS_GROUP_PARAMS = {
  New: { status: "New" },
  Connected: { status: "Connected" },
  Followups: { statuses: "Followups,Follow Up" },
};

const DNP_STATUSES_SET = new Set(["Not Connected", "Not Interested", "Invalid"]);

const getStatusOrFallback = (selectedStatus, fallbackStatuses) => {
  if (selectedStatus && selectedStatus !== "All") {
    return { status: selectedStatus };
  }
  return { statuses: fallbackStatuses };
};

const TAB_PARAM_HANDLERS = {
  leads: ({ leadStatusGroup }) => LEADS_GROUP_PARAMS[leadStatusGroup] || {},
  interested: ({ statusFilter }) => getStatusOrFallback(statusFilter, "Interested,Follow Up,Followups"),
  dnp: ({ selectedDnpStatus }) => getStatusOrFallback(selectedDnpStatus, "Not Connected,Not Interested,Invalid"),
  converted: ({ statusFilter }) => getStatusOrFallback(statusFilter, "Converted,Onboarded"),
  favourite: ({ statusFilter }) => {
    const params = { isFavourite: "true" };
    if (statusFilter && statusFilter !== "All") {
      params.status = statusFilter;
    }
    return params;
  },
};

const getTabStatusParams = (activeTab, filters) => {
  const handler = TAB_PARAM_HANDLERS[activeTab];
  return handler ? handler(filters) : {};
};

const buildCompanyQueryParamsHelper = (targetPage, options) => {
  const { debouncedSearch, selectedLeadSet, selectedLeadDivision, activeTab, leadStatusGroup, statusFilter, selectedDnpStatus } = options;

  const params = {
    page: targetPage,
    pageSize: 10,
    paginated: "true",
  };

  const trimmedSearch = (debouncedSearch || "").trim();
  if (trimmedSearch) {
    params.search = trimmedSearch;
  }
  if (selectedLeadSet) {
    params.setLabel = selectedLeadSet;
  }
  if (selectedLeadDivision) {
    params.division = selectedLeadDivision;
  }

  const tabParams = getTabStatusParams(activeTab, { leadStatusGroup, statusFilter, selectedDnpStatus });
  return Object.assign(params, tabParams);
};

const mapClientItem = (c) => ({
  id: c._id || c.clientId || c.companyName,
  name: c.companyName || c.leadCompanyName || "Client Account",
  category: c.description || "Onboarded Client",
  division: c.description || "Onboarded Client",
  directorCount: 1,
  city: c.address || "India",
  leadSet: c.clientId || "Clients",
  aiSummary: c.description || `Onboarded Client Record (${c.clientId || ""})`,
  contacts: [
    {
      _id: c._id || c.clientId || (c.companyName ? `client_${c.companyName}` : "client_contact"),
      clientId: c.clientId,
      companyName: c.companyName || c.leadCompanyName || "Client Account",
      leadCompanyName: c.companyName || c.leadCompanyName || "Client Account",
      contactName: c.primaryContactName || c.primaryContact || c.name || "Primary Contact",
      contactNumber: c.primaryPhone || c.phone || "",
      directorEmailAddress: c.primaryEmail || c.email || "",
      designation: "Key Account Director",
      status: c.status || "Onboarded",
      isStarred: true,
      isFavourite: true,
      remarks: Array.isArray(c.remarks) && c.remarks.length > 0 ? c.remarks : [{ text: `Onboarded Client ID: ${c.clientId || ""}`, date: c.createdAt || "" }],
      callSummary: c.callSummary || { total: 1, connected: 1, lastCallDate: c.updatedAt || "" },
    },
  ],
});

const mapCompanyItem = (c) => ({
  id: c.name || c.id,
  name: c.name || c.id,
  directorCount: c.directorCount ?? c.count ?? 0,
  category: c.division || c.category || "General",
  division: c.division || c.category || "General",
  city: c.city || "India",
  leadSet: c.leadSet || "General",
  cin: c.cin || "",
  dateOfIncorporation: c.dateOfIncorporation || "",
});

const loadClientsData = async (effectiveCompanyCode, setters) => {
  const { setCompanies, setHasMore, setPage, setTotalCompanies, setSelectedCompanyId, setCompanyDetailsCache } = setters;
  const res = await getClients(effectiveCompanyCode);
  if (!res?.success) return;

  let clientItems = [];
  if (Array.isArray(res.clients)) {
    clientItems = res.clients;
  } else if (Array.isArray(res.items)) {
    clientItems = res.items;
  }
  const mapped = clientItems.map(mapClientItem);

  setCompanies(mapped);
  setHasMore(false);
  setPage(1);
  setTotalCompanies(mapped.length);

  if (typeof setCompanyDetailsCache === "function" && mapped.length > 0) {
    const cacheMap = {};
    mapped.forEach((item) => {
      if (item.id) cacheMap[item.id] = item;
      if (item.name) cacheMap[item.name] = item;
    });
    setCompanyDetailsCache((prev) => ({ ...prev, ...cacheMap }));
  }

  if (mapped.length > 0) {
    setSelectedCompanyId((prev) => (prev && mapped.some((m) => m.id === prev) ? prev : mapped[0].id));
  }
};

const loadLeadsAndSetsData = async (effectiveCompanyCode, effectiveEmployeeId, queryParams, setters, fetchCompanyDetails) => {
  const { setAvailableLeadSets, setCompanies, setPage, setHasMore, setTotalCompanies, setSelectedCompanyId } = setters;

  const setsPromise = effectiveEmployeeId ? getEmployeeLeadSets(effectiveCompanyCode, effectiveEmployeeId) : getAdminLeadSets(effectiveCompanyCode);

  const companiesPromise = effectiveEmployeeId ? getEmployeeLeadCompanies(effectiveCompanyCode, effectiveEmployeeId, queryParams) : getAdminLeadCompanies(effectiveCompanyCode, queryParams);

  const [setsRes, compRes] = await Promise.all([setsPromise, companiesPromise]);

  if (setsRes?.success && Array.isArray(setsRes.sets)) {
    setAvailableLeadSets(setsRes.sets.filter(Boolean));
  }

  if (compRes?.success && Array.isArray(compRes.companies)) {
    const compList = compRes.companies.map(mapCompanyItem);
    setCompanies(compList);
    setPage(1);
    setHasMore(Boolean(compRes.hasMore));
    setTotalCompanies(compRes.total || compList.length);

    if (compList.length > 0) {
      setSelectedCompanyId((prev) => {
        if (prev && compList.some((c) => c.id === prev)) {
          return prev;
        }
        const firstComp = compList[0];
        fetchCompanyDetails(firstComp.name);
        return firstComp.id;
      });
    } else {
      setSelectedCompanyId("");
    }
  }
};

const filterByRemark = (contacts, selectedRemark) => {
  if (!selectedRemark) return contacts;
  const target = selectedRemark.toLowerCase();
  return contacts.filter((c) => {
    if (!Array.isArray(c.remarks)) return false;
    return c.remarks.some((r) => {
      const text = typeof r === "string" ? r : r?.text || r?.remark || r?.comment || "";
      return text.toLowerCase().includes(target);
    });
  });
};

const filterByTabAndStatus = (contacts, options) => {
  const { activeTab, leadStatusGroup, statusFilter, selectedDnpStatus, selectedDateStr } = options;

  switch (activeTab) {
    case "leads":
      if (leadStatusGroup === "New") return contacts.filter((c) => c.status === "New");
      if (leadStatusGroup === "Connected") return contacts.filter((c) => c.status === "Connected");
      if (leadStatusGroup === "Followups") return contacts.filter((c) => c.status === "Followups" || c.status === "Follow Up");
      return contacts;

    case "interested": {
      const base = contacts.filter((c) => c.status === "Interested" || c.status === "Follow Up" || c.status === "Followups");
      return statusFilter !== "All" ? base.filter((c) => c.status === statusFilter) : base;
    }

    case "dnp": {
      const base = contacts.filter((c) => DNP_STATUSES_SET.has(c.status));
      return selectedDnpStatus !== "All" ? base.filter((c) => c.status === selectedDnpStatus) : base;
    }

    case "converted": {
      const base = contacts.filter((c) => c.status === "Converted" || c.status === "Onboarded");
      return statusFilter !== "All" ? base.filter((c) => c.status === statusFilter) : base;
    }

    case "favourite": {
      const base = contacts.filter((c) => c.isFavourite || c.isStarred);
      return statusFilter !== "All" ? base.filter((c) => c.status === statusFilter) : base;
    }

    case "today-calls":
      return contacts.filter((ct) => {
        const uDate = ct.updatedAt ? dayjs(ct.updatedAt).format("YYYY-MM-DD") : null;
        const cDate = ct.callSummary?.lastCallDate ? dayjs(ct.callSummary.lastCallDate).format("YYYY-MM-DD") : null;
        const matchesDate = uDate === selectedDateStr || cDate === selectedDateStr;
        const matchesStatus = statusFilter === "All" || ct.status === statusFilter;
        return matchesDate && matchesStatus;
      });

    default:
      return contacts;
  }
};

const filterCompanyContacts = (comp, options) => {
  if (!comp || !Array.isArray(comp.contacts)) return [];
  const remarkFiltered = filterByRemark(comp.contacts, options.selectedProductRemark);
  return filterByTabAndStatus(remarkFiltered, options);
};

const ResizableHeaderCell = (props) => {
  const { onResize, width, children, ...restProps } = props;

  if (!width) {
    return <th {...restProps}>{children}</th>;
  }

  const handleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.pageX;
    const startWidth = width;

    const handleMouseMove = (moveEvent) => {
      const deltaX = moveEvent.pageX - startX;
      const nextWidth = Math.max(90, startWidth + deltaX);
      if (onResize) {
        onResize(nextWidth);
      }
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  return (
    <th {...restProps} style={{ ...restProps.style, width, position: "relative" }}>
      {children}
      <button type="button" aria-label="Resize column" className="table-resizable-handle" onMouseDown={handleMouseDown} />
    </th>
  );
};

// Initial column widths configuration
const INITIAL_COLUMN_WIDTHS = {
  contactName: 190,
  contactNumber: 160,
  directorEmailAddress: 230,
  status: 160,
  remarks: 260,
  actions: 290,
};

function LeadContactsView({ activeContacts, viewMode, tableColumns, totalTableWidth, activeCompany, handleUpdateLeadStatus, handleAddRemark, handleToggleStar, handleToggleFavourite, setDetailDrawerLead, handleScheduleFollowup, availableCallStatuses, getAvailableStatusesForLead, handleOpenMail, loadingDetails, onOpenQuotation, onOpenProposal }) {
  if (activeContacts.length === 0) {
    return <Empty description={loadingDetails ? "Loading records..." : "No records match the selected filter in this company."} style={{ padding: "4rem 0" }} />;
  }

  if (viewMode === "table") {
    return (
      <Table
        columns={tableColumns}
        dataSource={activeContacts}
        rowKey="_id"
        pagination={false}
        className="leads-antd-table"
        size="middle"
        scroll={{ x: totalTableWidth }}
        components={{
          header: {
            cell: ResizableHeaderCell,
          },
        }}
      />
    );
  }

  return (
    <LeadCardGrid
      leads={activeContacts}
      company={activeCompany}
      onStatusChange={handleUpdateLeadStatus}
      onAddRemark={handleAddRemark}
      onToggleStar={handleToggleStar}
      onToggleFavourite={handleToggleFavourite}
      onOpenHistory={(lead) => setDetailDrawerLead(lead)}
      onScheduleFollowup={handleScheduleFollowup}
      availableStatuses={availableCallStatuses}
      getAvailableStatusesForLead={getAvailableStatusesForLead}
      onOpenMail={handleOpenMail}
      onOpenQuotation={onOpenQuotation}
      onOpenProposal={onOpenProposal}
    />
  );
}

async function createMultipleLeadDirectors(companyCode, basePayload, directors) {
  let firstResult = null;
  for (const dir of directors) {
    const nameParts = (dir.contactName || "").trim().split(" ");
    const dirPayload = {
      ...basePayload,
      contactName: (dir.contactName || "").trim(),
      contactNumber: (dir.contactNumber || "").trim(),
      directorEmailAddress: (dir.directorEmailAddress || "").trim(),
      designation: (dir.designation || "Director").trim(),
      directorFirstName: dir.directorFirstName || nameParts[0] || "",
      directorLastName: dir.directorLastName || nameParts.slice(1).join(" ") || "",
      directorDin: dir.directorDin || "",
    };
    delete dirPayload.directors;
    // eslint-disable-next-line no-await-in-loop
    const res = await createLead(companyCode, dirPayload);
    if (!firstResult) firstResult = res;
    if (res?.success === false) {
      message.error(res?.message || `Failed to create director: ${dir.contactName}`);
    }
  }
  return firstResult;
}

function LeadsTemplate({ activeTab = "leads", role = "admin", employeeId = null, employee = null, searchQuery = "" }) {
  const companyInfo = useSelector((state) => state.executor?.companyInfo);
  const reduxCompanyCode = companyInfo?.companyCode;
  const user = useSelector((state) => state.session?.settings?.userInfo);
  const callStatus = companyInfo?.callStatus;
  const availableCallStatuses = (Array.isArray(callStatus) && callStatus.length > 0 ? callStatus : AVAILABLE_STATUSES).filter((st) => {
    const norm = (st || "").toLowerCase();
    return norm !== "closed lost" && norm !== "converted";
  });
  const reduxProductRemarks = companyInfo?.productRemarks;
  const availableProductRemarks = Array.isArray(reduxProductRemarks) && reduxProductRemarks.length > 0 ? reduxProductRemarks : PRODUCT_REMARKS_CONFIG;
  const effectiveCompanyCode = reduxCompanyCode || user?.companyCode || getCookie("companyCode") || "";

  const effectiveRole = role || user?.role || getCookie("role") || "company_admin";
  const isEmployee = effectiveRole === "employee" || effectiveRole === "sales_rep";
  const effectiveEmployeeId = employeeId || employee?.id || employee?._id || employee?.employeeId || (isEmployee ? user?.id || user?._id || user?.employeeId || getCookie("userId") : null);
  const effectiveEmployeeName = employee?.name || user?.name || user?.fullName || "Sales Rep";

  const [companies, setCompanies] = useState([]);
  const [companyDetailsCache, setCompanyDetailsCache] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalCompanies, setTotalCompanies] = useState(0);

  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [companySearch, setCompanySearch] = useState(searchQuery || "");
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery || "");
  const [viewMode, setViewMode] = useState("table"); // 'table' | 'grid'
  const [colWidths, setColWidths] = useState(INITIAL_COLUMN_WIDTHS);

  // Sync search query from TopNavbar Global Search
  useEffect(() => {
    if (searchQuery !== undefined && searchQuery !== companySearch) {
      setCompanySearch(searchQuery);
    }
  }, [searchQuery, companySearch]);

  // Debounce search input to prevent rapid unnecessary fetches
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(companySearch);
    }, 350);
    return () => clearTimeout(handler);
  }, [companySearch]);

  // Filter States
  const [selectedLeadSet, setSelectedLeadSet] = useState("");
  const [availableLeadSets, setAvailableLeadSets] = useState([]);
  const [selectedLeadDivision, setSelectedLeadDivision] = useState("");
  const [selectedProductRemark, setSelectedProductRemark] = useState("");
  const [leadStatusGroup, setLeadStatusGroup] = useState("All");
  const [selectedDnpStatus, setSelectedDnpStatus] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [historyDate, setHistoryDate] = useState(() => dayjs());

  // Table Inline Inputs & Copy Feedback
  const [tableRemarkInputs, setTableRemarkInputs] = useState({});
  const [copiedId, setCopiedId] = useState(null);

  // Modals & Drawers State
  const [isAddLeadModalOpen, setIsAddLeadModalOpen] = useState(false);
  const [isAiSummaryModalOpen, setIsAiSummaryModalOpen] = useState(false);
  const [isFullViewOpen, setIsFullViewOpen] = useState(false);
  const [detailDrawerLead, setDetailDrawerLead] = useState(null);
  const [quotationModalOpen, setQuotationModalOpen] = useState(false);
  const [proposalModalOpen, setProposalModalOpen] = useState(false);
  const [dealModalOpen, setDealModalOpen] = useState(false);
  const [selectedLeadForDeal, setSelectedLeadForDeal] = useState(null);
  const [selectedLeadForAction, setSelectedLeadForAction] = useState(null);
  const [mailDrawerOpen, setMailDrawerOpen] = useState(false);
  const [mailLead, setMailLead] = useState(null);
  const [activityModalOpen, setActivityModalOpen] = useState(false);
  const [selectedLeadForActivity, setSelectedLeadForActivity] = useState(null);
  const [activityDefaultType, setActivityDefaultType] = useState("call");

  const handleOpenMail = useCallback((leadRecord) => {
    setMailLead(leadRecord);
    setMailDrawerOpen(true);
  }, []);

  const companyDetailsCacheRef = React.useRef({});
  useEffect(() => {
    companyDetailsCacheRef.current = companyDetailsCache;
  }, [companyDetailsCache]);

  // ── ON-DEMAND FULL COMPANY DETAILS FETCH ────────────────────────
  const fetchCompanyDetails = useCallback(
    async (companyName) => {
      if (!companyName || !effectiveCompanyCode) return;
      if (companyDetailsCacheRef.current[companyName]?.contacts?.length) {
        return; // Already cached
      }

      setLoadingDetails(true);
      try {
        const res = await getCompanyFullDetails(effectiveCompanyCode, companyName, effectiveEmployeeId);
        if (res?.success && res.company) {
          const compData = res.company;
          setCompanyDetailsCache((prev) => ({
            ...prev,
            [companyName]: compData,
            ...(compData.id ? { [compData.id]: compData } : {}),
            ...(compData._id ? { [compData._id]: compData } : {}),
          }));
        }
      } catch (err) {
        console.error("Failed to fetch full company details:", err);
      } finally {
        setLoadingDetails(false);
      }
    },
    [effectiveCompanyCode, effectiveEmployeeId],
  );

  // ── BUILD COMPANY QUERY PARAMS FOR BACKEND PAGINATION ───────────
  const buildCompanyQueryParams = useCallback(
    (targetPage = 1) => {
      return buildCompanyQueryParamsHelper(targetPage, {
        debouncedSearch,
        selectedLeadSet,
        selectedLeadDivision,
        activeTab,
        leadStatusGroup,
        statusFilter,
        selectedDnpStatus,
      });
    },
    [debouncedSearch, selectedLeadSet, selectedLeadDivision, activeTab, leadStatusGroup, statusFilter, selectedDnpStatus],
  );

  const isClientTab = activeTab === "our_clients" || activeTab === "clients" || activeTab === "crm_clients" || activeTab === "crm_client_onboard_requests";

  // ── LOAD INITIAL 10 COMPANIES (PAGE 1) ──────────────────────────
  const loadInitialData = useCallback(async () => {
    if (!effectiveCompanyCode) return;
    setLoading(true);
    try {
      const setters = {
        setAvailableLeadSets,
        setCompanies,
        setHasMore,
        setPage,
        setTotalCompanies,
        setSelectedCompanyId,
        setCompanyDetailsCache,
      };

      if (isClientTab) {
        await loadClientsData(effectiveCompanyCode, setters);
      } else {
        const params = buildCompanyQueryParams(1);
        await loadLeadsAndSetsData(effectiveCompanyCode, effectiveEmployeeId, params, setters, fetchCompanyDetails);
      }
    } catch (err) {
      console.error("Failed to load initial leads/companies:", err);
    } finally {
      setLoading(false);
    }
  }, [effectiveCompanyCode, isClientTab, effectiveEmployeeId, buildCompanyQueryParams, fetchCompanyDetails]);

  // ── LOAD MORE COMPANIES ON INFINITE SCROLL ──────────────────────
  const loadMoreCompanies = useCallback(async () => {
    if (loadingMore || loading || !hasMore || !effectiveCompanyCode) return;
    setLoadingMore(true);
    try {
      const nextPage = page + 1;
      const params = buildCompanyQueryParams(nextPage);
      const res = effectiveEmployeeId ? await getEmployeeLeadCompanies(effectiveCompanyCode, effectiveEmployeeId, params) : await getAdminLeadCompanies(effectiveCompanyCode, params);

      if (res?.success && Array.isArray(res.companies)) {
        const newComps = res.companies.map(mapCompanyItem);

        setCompanies((prev) => {
          const existingIds = new Set(prev.map((c) => c.id));
          const filtered = newComps.filter((c) => !existingIds.has(c.id));
          return [...prev, ...filtered];
        });
        setPage(nextPage);
        setHasMore(Boolean(res.hasMore));
        setTotalCompanies(res.total || 0);
      }
    } catch (err) {
      console.error("Failed to load more companies:", err);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, loading, hasMore, effectiveCompanyCode, page, buildCompanyQueryParams, effectiveEmployeeId]);

  // Infinite Scroll Handler for Left Company Rail
  const handleRailScroll = (e) => {
    const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 30) {
      if (hasMore && !loadingMore && !loading) {
        loadMoreCompanies();
      }
    }
  };

  // Select Company Handler: Updates selection and triggers on-demand full details fetch
  const handleSelectCompany = (comp) => {
    setSelectedCompanyId(comp.id);
    if (!isClientTab || !comp.contacts || comp.contacts.length === 0) {
      fetchCompanyDetails(comp.name);
    }
  };

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const selectedDateStr = historyDate ? historyDate.format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD");

  // Filter Contacts for any Company based on active Tab, status group, remark, and date filters
  const getCompanyMatchingContacts = useCallback(
    (comp) => {
      return filterCompanyContacts(comp, {
        activeTab,
        selectedProductRemark,
        leadStatusGroup,
        selectedDnpStatus,
        statusFilter,
        selectedDateStr,
      });
    },
    [activeTab, selectedProductRemark, leadStatusGroup, selectedDnpStatus, statusFilter, selectedDateStr],
  );

  // Metrics & status counts for Today's History Tab
  const todayCallMetrics = useMemo(() => {
    if (activeTab !== "today-calls") return { total: 0, connected: 0, statusCluster: [] };
    let total = 0;
    let connected = 0;
    const statusMap = {};

    Object.values(companyDetailsCache).forEach((c) => {
      if (Array.isArray(c?.contacts)) {
        c.contacts.forEach((ct) => {
          const uDate = ct.updatedAt ? dayjs(ct.updatedAt).format("YYYY-MM-DD") : null;
          const cDate = ct.callSummary?.lastCallDate ? dayjs(ct.callSummary.lastCallDate).format("YYYY-MM-DD") : null;
          if (uDate === selectedDateStr || cDate === selectedDateStr) {
            total += 1;
            if (ct.status === "Connected" || ct.status === "Converted" || (ct.callSummary?.connected && ct.callSummary.connected > 0)) {
              connected += 1;
            }
            const st = ct.status || "New";
            statusMap[st] = (statusMap[st] || 0) + 1;
          }
        });
      }
    });

    const statusCluster = Object.entries(statusMap).map(([status, count]) => ({ status, count }));
    return { total, connected, statusCluster };
  }, [companyDetailsCache, activeTab, selectedDateStr]);

  // Filtered Company List in Left Rail
  const filteredCompanies = useMemo(() => {
    let list = [...companies];
    if (selectedProductRemark) {
      list = list.filter((c) => {
        if (!companyDetailsCache[c.id]) return true;
        return getCompanyMatchingContacts(companyDetailsCache[c.id]).length > 0;
      });
    }
    return list;
  }, [companies, selectedProductRemark, companyDetailsCache, getCompanyMatchingContacts]);

  // Active Company based on selectedCompanyId & memory cache
  const activeCompany = useMemo(() => {
    const findCached = (id, name) => {
      if (id && companyDetailsCache[id]) return companyDetailsCache[id];
      if (name && companyDetailsCache[name]) return companyDetailsCache[name];
      return null;
    };

    if (!selectedCompanyId) {
      if (filteredCompanies.length > 0) {
        const first = filteredCompanies[0];
        return findCached(first.id, first.name) || { ...first, contacts: first.contacts || [] };
      }
      return null;
    }

    const cached = findCached(selectedCompanyId, null);
    if (cached) return cached;

    const foundSummary = filteredCompanies.find((c) => c.id === selectedCompanyId || c.name === selectedCompanyId);
    if (foundSummary) {
      const cachedByName = findCached(null, foundSummary.name);
      if (cachedByName) return cachedByName;
      return { ...foundSummary, contacts: foundSummary.contacts || [] };
    }
    return null;
  }, [selectedCompanyId, companyDetailsCache, filteredCompanies]);

  // Contacts for the Selected Active Company (show all directors of the company)
  const activeContacts = useMemo(() => {
    if (!activeCompany || !Array.isArray(activeCompany.contacts)) return [];
    return activeCompany.contacts;
  }, [activeCompany]);

  // Current lead for the detail drawer (synced with memory cache)
  const currentDetailLead = useMemo(() => {
    if (!detailDrawerLead) return null;
    return activeContacts.find((l) => l._id === detailDrawerLead._id) || detailDrawerLead;
  }, [detailDrawerLead, activeContacts]);

  // Handle Copy to Clipboard
  const handleCopy = (text, id) => {
    void navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopiedId(id);
        message.success("Copied to clipboard!");
        setTimeout(() => setCopiedId(null), 2000);
      })
      .catch((err) => {
        console.error("Clipboard copy failed:", err);
        message.error("Failed to copy to clipboard.");
      });
  };

  // Lead State Mutators (Synchronized with Memory Cache & Database)
  const handleScheduleFollowup = useCallback(
    (leadRecord, type = "call") => {
      if (!leadRecord) return;
      setSelectedLeadForActivity({
        ...leadRecord,
        leadCompanyName: activeCompany?.name || leadRecord.leadCompanyName || leadRecord.companyName,
      });
      setActivityDefaultType(type);
      setActivityModalOpen(true);
    },
    [activeCompany?.name],
  );
  const persistLeadStatusUpdate = useCallback(
    async (leadId, newStatus) => {
      try {
        const res = await updateLead(effectiveCompanyCode, leadId, { status: newStatus });
        if (res?.success !== false) {
          setCompanyDetailsCache((prev) => {
            const cur = prev[selectedCompanyId];
            if (!cur || !Array.isArray(cur.contacts)) return prev;
            return {
              ...prev,
              [selectedCompanyId]: {
                ...cur,
                contacts: cur.contacts.map((l) => (l._id === leadId ? { ...l, status: newStatus } : l)),
              },
            };
          });
          message.success(`Status updated to ${newStatus}`);
        } else {
          message.error(res?.message || "Failed to update status.");
        }
      } catch (err) {
        console.error(err);
        message.error("Failed to update status.");
      }
    },
    [effectiveCompanyCode, selectedCompanyId],
  );

  const handleUpdateLeadStatus = useCallback(
    async (leadId, newStatus) => {
      if (newStatus === "ADD_DEAL") {
        const curContacts = (selectedCompanyId && companyDetailsCache[selectedCompanyId]?.contacts) || activeContacts;
        const targetLead = curContacts.find((l) => l._id === leadId);
        if (targetLead) {
          setSelectedLeadForDeal(targetLead);
          setDealModalOpen(true);
        }
        return;
      }

      const normalizedStatus = (newStatus || "").toLowerCase().replace(/[-_]/g, " ").trim();
      if (normalizedStatus === "follow up" || normalizedStatus === "followup") {
        const curContacts = (selectedCompanyId && companyDetailsCache[selectedCompanyId]?.contacts) || activeContacts;
        const targetLead = curContacts.find((l) => l._id === leadId);
        if (targetLead) {
          handleScheduleFollowup(targetLead, "call");
        }
        return;
      }

      await persistLeadStatusUpdate(leadId, newStatus);
    },
    [selectedCompanyId, companyDetailsCache, activeContacts, handleScheduleFollowup, persistLeadStatusUpdate],
  );

  const handleSaveNewDeal = async (dealPayload) => {
    try {
      const res = await createDeal(effectiveCompanyCode, dealPayload);
      if (res?.success !== false) {
        message.success("Deal created in pipeline successfully!");
        if (selectedLeadForDeal?._id) {
          handleUpdateLeadStatus(selectedLeadForDeal._id, "Pipeline");
        }
        setCompanyDetailsCache({});
        loadInitialData();
        return res;
      } else {
        message.error(res?.message || "Failed to create deal.");
        return null;
      }
    } catch (err) {
      console.error("Deal save error:", err);
      message.error("An error occurred while creating deal.");
      throw err;
    }
  };

  const handleToggleStar = useCallback(
    async (leadId) => {
      const lead = activeContacts.find((l) => l._id === leadId);
      if (!lead) return;
      const nextStar = !lead.isStarred;
      try {
        await updateLead(effectiveCompanyCode, leadId, { isStarred: nextStar });
        setCompanyDetailsCache((prev) => {
          const cur = prev[selectedCompanyId];
          if (!cur || !Array.isArray(cur.contacts)) return prev;
          return {
            ...prev,
            [selectedCompanyId]: {
              ...cur,
              contacts: cur.contacts.map((l) => (l._id === leadId ? { ...l, isStarred: nextStar } : l)),
            },
          };
        });
      } catch (err) {
        console.error(err);
      }
    },
    [activeContacts, effectiveCompanyCode, selectedCompanyId],
  );

  const handleToggleFavourite = useCallback(
    async (leadId) => {
      const lead = activeContacts.find((l) => l._id === leadId);
      if (!lead) return;
      const nextFav = !lead.isFavourite;
      try {
        await updateLead(effectiveCompanyCode, leadId, { isFavourite: nextFav });
        setCompanyDetailsCache((prev) => {
          const cur = prev[selectedCompanyId];
          if (!cur || !Array.isArray(cur.contacts)) return prev;
          return {
            ...prev,
            [selectedCompanyId]: {
              ...cur,
              contacts: cur.contacts.map((l) => (l._id === leadId ? { ...l, isFavourite: nextFav } : l)),
            },
          };
        });
      } catch (err) {
        console.error(err);
      }
    },
    [activeContacts, effectiveCompanyCode, selectedCompanyId],
  );

  const handleAddRemark = useCallback(
    async (leadId, remarkText) => {
      try {
        const res = await addLeadRemark(effectiveCompanyCode, leadId, remarkText);
        if (res?.success !== false) {
          const newEntry = {
            text: remarkText,
            date: new Date().toISOString().replace("T", " ").slice(0, 16),
          };
          setCompanyDetailsCache((prev) => {
            const cur = prev[selectedCompanyId];
            if (!cur || !Array.isArray(cur.contacts)) return prev;
            return {
              ...prev,
              [selectedCompanyId]: {
                ...cur,
                contacts: cur.contacts.map((l) => (l._id === leadId ? { ...l, remarks: [newEntry, ...(l.remarks || [])] } : l)),
              },
            };
          });
          message.success("Remark added successfully!");
        }
      } catch (err) {
        console.error(err);
        message.error("Failed to save remark.");
      }
    },
    [effectiveCompanyCode, selectedCompanyId],
  );

  const handleAddLead = async (formData) => {
    try {
      const compName = formData.leadCompanyName || formData.companyName;
      const basePayload = {
        ...formData,
        companyCode: formData.companyCode || effectiveCompanyCode,
        leadCompanyName: compName,
        companyName: compName,
        assignedEmployeeId: formData.assignedEmployeeId || effectiveEmployeeId || user?.id || user?._id || user?.employeeId || getCookie("userId") || getCookie("employeeId"),
        assignedEmployeePhone: formData.assignedEmployeePhone || employee?.mobile || "",
      };

      const directors = Array.isArray(formData.directors) && formData.directors.length > 0 ? formData.directors : null;

      if (directors) {
        const firstRes = await createMultipleLeadDirectors(effectiveCompanyCode, basePayload, directors);
        message.success(
          directors.length > 1
            ? `Lead created with ${directors.length} directors successfully!`
            : "Lead created successfully!"
        );
        setCompanyDetailsCache({});
        loadInitialData();
        return firstRes;
      }

      const res = await createLead(effectiveCompanyCode, basePayload);
      if (res?.success !== false) {
        message.success("Lead created successfully!");
        setCompanyDetailsCache({});
        loadInitialData();
        return res;
      }
      message.error(res?.message || "Failed to create lead.");
      return null;
    } catch (err) {
      console.error(err);
      message.error("Failed to create lead.");
      throw err;
    }
  };

  const handleImportLeads = async (importedLeads, setLabel) => {
    try {
      const fallbackEmpId = effectiveEmployeeId || user?.id || user?._id || user?.employeeId || getCookie("userId") || getCookie("employeeId");
      const enrichedLeads = importedLeads.map((l) => ({
        ...l,
        companyCode: l.companyCode || effectiveCompanyCode,
        assignedEmployeeId: l.assignedEmployeeId || fallbackEmpId,
        assignedEmployeePhone: l.assignedEmployeePhone || employee?.mobile || "",
      }));
      const res = await addBulkLeads(effectiveCompanyCode, enrichedLeads, { setLabel });
      if (res?.success !== false) {
        message.success(`Successfully imported ${enrichedLeads.length} leads!`);
        setCompanyDetailsCache({});
        loadInitialData();
        return res;
      } else {
        message.error(res?.message || "Bulk import failed.");
        return null;
      }
    } catch (err) {
      console.error(err);
      message.error("Failed to import leads.");
      throw err;
    }
  };

  const handleResize = useCallback(
    (key) => (width) => {
      setColWidths((prev) => ({
        ...prev,
        [key]: width,
      }));
    },
    [],
  );

  // Dynamic Available Statuses for Lead depending on Status Group & Active Tab
  const getAvailableStatusesForLead = useCallback(
    (lead) => {
      const baseStatuses = availableCallStatuses.filter((s) => {
        const norm = (s || "").toLowerCase();
        return norm !== "closed lost" && norm !== "converted";
      });

      let result = baseStatuses;

      if (activeTab === "leads") {
        if (leadStatusGroup === "Connected") {
          result = baseStatuses.filter((s) => {
            const norm = (s || "").toLowerCase();
            return norm !== "new" && norm !== "invalid" && norm !== "not connected";
          });
        } else if (leadStatusGroup === "Followups") {
          result = baseStatuses.filter((s) => {
            const norm = (s || "").toLowerCase();
            return norm !== "new" && norm !== "invalid" && norm !== "not connected" && norm !== "connected" && norm !== "contacted";
          });
        }
      }

      const currentStatus = lead?.status;
      if (currentStatus && !result.includes(currentStatus)) {
        return [currentStatus, ...result];
      }

      return result;
    },
    [availableCallStatuses, activeTab, leadStatusGroup],
  );

  // Dynamic Adjustable Ant Design Table Columns
  const tableColumns = useMemo(
    () => [
      {
        title: "Lead Name",
        dataIndex: "contactName",
        key: "contactName",
        width: colWidths.contactName,
        onHeaderCell: () => ({
          width: colWidths.contactName,
          onResize: handleResize("contactName"),
        }),
        render: (text, record) => (
          <Flex align="center" gap={8} style={{ whiteSpace: "nowrap" }}>
            <Text strong style={{ color: "#0f172a", fontSize: "0.875rem" }}>
              {text}
            </Text>
            {record.isStarred && <span className="spoc-badge">SPOC</span>}
          </Flex>
        ),
      },
      {
        title: "Contact",
        dataIndex: "contactNumber",
        key: "contactNumber",
        width: colWidths.contactNumber,
        onHeaderCell: () => ({
          width: colWidths.contactNumber,
          onResize: handleResize("contactNumber"),
        }),
        render: (text, record) => (
          <div className="copyable-cell">
            <Text style={{ fontSize: "0.85rem", whiteSpace: "nowrap" }}>{text || "-"}</Text>
            {text && (
              <Tooltip title="Copy number">
                <Button type="text" size="small" className="copy-icon-btn" icon={copiedId === `phone_${record._id}` ? <CheckOutlined style={{ color: "#10b981" }} /> : <CopyOutlined />} onClick={() => handleCopy(text, `phone_${record._id}`)} />
              </Tooltip>
            )}
          </div>
        ),
      },
      {
        title: "Email",
        dataIndex: "directorEmailAddress",
        key: "directorEmailAddress",
        width: colWidths.directorEmailAddress,
        onHeaderCell: () => ({
          width: colWidths.directorEmailAddress,
          onResize: handleResize("directorEmailAddress"),
        }),
        render: (text, record) => (
          <div className="copyable-cell">
            <Text style={{ fontSize: "0.85rem", whiteSpace: "nowrap" }}>{text || "-"}</Text>
            {text && (
              <Tooltip title="Copy email">
                <Button type="text" size="small" className="copy-icon-btn" icon={copiedId === `email_${record._id}` ? <CheckOutlined style={{ color: "#10b981" }} /> : <CopyOutlined />} onClick={() => handleCopy(text, `email_${record._id}`)} />
              </Tooltip>
            )}
          </div>
        ),
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: colWidths.status,
        onHeaderCell: () => ({
          width: colWidths.status,
          onResize: handleResize("status"),
        }),
        render: (status, record) => (
          <Select
            size="small"
            value={status}
            onChange={(val) => handleUpdateLeadStatus(record._id, val)}
            className="table-status-select"
            style={{ width: 130 }}
            options={[
              ...getAvailableStatusesForLead(record).map((st) => ({
                value: st,
                label: st,
              })),
              {
                value: "DIVIDER",
                label: "──────",
                disabled: true,
              },
              {
                value: "ADD_DEAL",
                label: "+ Add a Deal",
              },
            ]}
          />
        ),
      },
      {
        title: "Remarks",
        key: "remarks",
        width: colWidths.remarks,
        onHeaderCell: () => ({
          width: colWidths.remarks,
          onResize: handleResize("remarks"),
        }),
        render: (_, record) => {
          const text = tableRemarkInputs[record._id] || "";
          return (
            <div className="inline-remark-box">
              <Input
                size="small"
                placeholder="Type remark..."
                value={text}
                onChange={(e) => setTableRemarkInputs((prev) => ({ ...prev, [record._id]: e.target.value }))}
                onPressEnter={() => {
                  if (text.trim()) {
                    handleAddRemark(record._id, text.trim());
                    setTableRemarkInputs((prev) => ({ ...prev, [record._id]: "" }));
                  }
                }}
                style={{ minWidth: 0 }}
              />
              <Button
                shape="circle"
                size="small"
                type="primary"
                className="remark-send-btn table-size"
                icon={<SendOutlined style={{ fontSize: 11, marginLeft: -1, marginTop: 1 }} />}
                disabled={!text.trim()}
                onClick={() => {
                  handleAddRemark(record._id, text.trim());
                  setTableRemarkInputs((prev) => ({ ...prev, [record._id]: "" }));
                }}
              />
              <Button type="link" size="small" style={{ fontSize: "0.75rem", padding: 0 }} onClick={() => setDetailDrawerLead(record)}>
                History
              </Button>
            </div>
          );
        },
      },
      {
        title: "Actions",
        key: "actions",
        width: colWidths.actions,
        onHeaderCell: () => ({
          width: colWidths.actions,
          onResize: handleResize("actions"),
        }),
        render: (_, record) => (
          <Space size={6} style={{ whiteSpace: "nowrap" }}>
            <Button
              size="small"
              type="default"
              icon={<FormOutlined />}
              onClick={() => {
                setSelectedLeadForAction({
                  _id: record._id,
                  leadId: record._id,
                  leadCompanyName: activeCompany?.name || "",
                  contactName: record.contactName,
                  contactNumber: record.contactNumber,
                  directorEmailAddress: record.directorEmailAddress,
                });
                setProposalModalOpen(true);
              }}
            >
              Proposal
            </Button>
            <Button
              size="small"
              type="default"
              icon={<CalculatorOutlined />}
              onClick={() => {
                setSelectedLeadForAction({
                  _id: record._id,
                  leadId: record._id,
                  leadCompanyName: activeCompany?.name || "",
                  contactName: record.contactName,
                  contactNumber: record.contactNumber,
                  directorEmailAddress: record.directorEmailAddress,
                });
                setQuotationModalOpen(true);
              }}
            >
              Quotation
            </Button>
            <Tooltip title="Schedule Activity / Follow-up">
              <Button size="small" type="text" icon={<CalendarOutlined style={{ color: "#2459d3" }} />} onClick={() => handleScheduleFollowup(record)} />
            </Tooltip>
            <Tooltip title="Send Email">
              <Button size="small" type="text" icon={<MailOutlined />} onClick={() => handleOpenMail(record)} />
            </Tooltip>
            <Tooltip title="Toggle Star / SPOC">
              <Button size="small" type="text" icon={<StarFilled style={{ color: record.isStarred ? "#eab308" : "#cbd5e1" }} />} onClick={() => handleToggleStar(record._id)} />
            </Tooltip>
            <Tooltip title="Toggle Favourite">
              <Button size="small" type="text" icon={<HeartFilled style={{ color: record.isFavourite ? "#ef4444" : "#cbd5e1" }} />} onClick={() => handleToggleFavourite(record._id)} />
            </Tooltip>
          </Space>
        ),
      },
    ],
    [colWidths, copiedId, handleResize, tableRemarkInputs, activeCompany, handleUpdateLeadStatus, handleScheduleFollowup, handleAddRemark, handleToggleStar, handleToggleFavourite, getAvailableStatusesForLead, handleOpenMail],
  );

  const totalTableWidth = useMemo(() => {
    return Object.values(colWidths).reduce((acc, w) => acc + w, 0);
  }, [colWidths]);

  return (
    <Spin spinning={loading}>
      <Flex vertical gap={16} className="leads-workspace-container">
        {/* ── 1. FILTER & CONTROLS BAR ── */}
        <div className="lead-filter-bar">
          <Flex justify="space-between" align="center" wrap="wrap" gap={16}>
            <Flex align="center" gap={16} wrap="wrap">
              {activeTab !== "remarks_filter" && (
                <>
                  {/* Lead Filter dropdown */}
                  <div>
                    <div className="mini-filter-label">Lead Filter</div>
                    <Select value={selectedLeadSet} onChange={(val) => setSelectedLeadSet(val)} placeholder="All Leads" style={{ minWidth: 170 }} options={[{ value: "", label: "All Leads" }, ...availableLeadSets.map((s) => ({ value: s, label: s }))]} />
                  </div>

                  {/* Segregation Filter */}
                  <div>
                    <div className="mini-filter-label">Lead Segregation</div>
                    <Select value={selectedLeadDivision} onChange={(val) => setSelectedLeadDivision(val)} placeholder="All Segments" style={{ minWidth: 180 }} options={[{ value: "", label: "All Segments" }, ...Array.from(new Set(companies.map((c) => c.category).filter(Boolean))).map((cat) => ({ value: cat, label: cat }))]} />
                  </div>
                </>
              )}

              {/* Remark / Certification Dropdown */}
              {activeTab === "remarks_filter" && (
                <div>
                  <div className="mini-filter-label">Remark / Certification *</div>
                  <Select
                    value={selectedProductRemark}
                    onChange={(val) => setSelectedProductRemark(val)}
                    placeholder="All Remarks / Certifications"
                    style={{ minWidth: 220 }}
                    allowClear
                    options={[
                      { value: "", label: "All Remarks / Certifications" },
                      ...availableProductRemarks.map((rem) => ({
                        value: rem,
                        label: rem,
                      })),
                    ]}
                  />
                </div>
              )}

              {/* Tab Specific Filters */}
              {activeTab === "leads" && (
                <div>
                  <div className="mini-filter-label">Status Group</div>
                  <Radio.Group value={leadStatusGroup} onChange={(e) => setLeadStatusGroup(e.target.value)} buttonStyle="solid" size="middle">
                    <Radio.Button value="All">All Leads</Radio.Button>
                    <Radio.Button value="New">New</Radio.Button>
                    <Radio.Button value="Connected">Connected</Radio.Button>
                    <Radio.Button value="Followups">Followups</Radio.Button>
                  </Radio.Group>
                </div>
              )}

              {activeTab === "dnp" && (
                <div>
                  <div className="mini-filter-label">DNP Filter</div>
                  <Radio.Group value={selectedDnpStatus} onChange={(e) => setSelectedDnpStatus(e.target.value)} buttonStyle="solid" size="middle">
                    <Radio.Button value="All">All DNP</Radio.Button>
                    <Radio.Button value="Not Connected">Not Connected</Radio.Button>
                    <Radio.Button value="Not Interested">Not Interested</Radio.Button>
                    <Radio.Button value="Invalid">Invalid</Radio.Button>
                  </Radio.Group>
                </div>
              )}

              {(activeTab === "interested" || activeTab === "converted" || activeTab === "favourite") && (
                <div>
                  <div className="mini-filter-label">Status Filter</div>
                  <Select value={statusFilter} onChange={(val) => setStatusFilter(val)} style={{ width: 140 }} options={[{ value: "All", label: "All Statuses" }, ...availableCallStatuses.map((st) => ({ value: st, label: st }))]} />
                </div>
              )}

              {activeTab === "today-calls" && (
                <>
                  <div>
                    <div className="mini-filter-label">Date</div>
                    <DatePicker value={historyDate} onChange={(d) => setHistoryDate(d || dayjs())} maxDate={dayjs()} allowClear={false} style={{ width: 140 }} />
                  </div>
                  <div>
                    <div className="mini-filter-label">Status Filter</div>
                    <Select value={statusFilter} onChange={(val) => setStatusFilter(val)} style={{ width: 150 }} options={[{ value: "All", label: "All Statuses" }, ...availableCallStatuses.map((st) => ({ value: st, label: st }))]} />
                  </div>
                </>
              )}
            </Flex>

            {/* Action Right: Refresh, + Add Lead and View Mode Toggle */}
            <Flex align="center" gap={12}>
              {activeTab === "leads" && (
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsAddLeadModalOpen(true)} style={{ height: 36 }}>
                  Add Lead
                </Button>
              )}

              <Segmented
                value={viewMode}
                onChange={(val) => setViewMode(val)}
                options={[
                  { value: "table", icon: <BarsOutlined /> },
                  { value: "grid", icon: <AppstoreOutlined /> },
                ]}
              />
            </Flex>
          </Flex>
        </div>

        {/* ── 2. TODAY CALL HISTORY KPI RIBBON (If today-calls tab) ── */}
        {activeTab === "today-calls" && (
          <div className="today-summary-ribbon">
            <Flex align="center" justify="space-between" wrap="wrap" gap={16}>
              <Flex align="center" gap={16} wrap="wrap">
                {/* Total Calls Card */}
                <div className="today-kpi-chip">
                  <div className="today-kpi-icon call">
                    <PhoneOutlined />
                  </div>
                  <div>
                    <Text type="secondary" style={{ fontSize: "0.725rem", textTransform: "uppercase", fontWeight: 600 }}>
                      Total Calls
                    </Text>
                    <div style={{ fontSize: "1.15rem", fontWeight: 700, color: "#0f172a" }}>{todayCallMetrics.total}</div>
                  </div>
                </div>

                {/* Connected Calls Card */}
                <div className="today-kpi-chip">
                  <div className="today-kpi-icon connected">
                    <CheckCircleOutlined />
                  </div>
                  <div>
                    <Text type="secondary" style={{ fontSize: "0.725rem", textTransform: "uppercase", fontWeight: 600 }}>
                      Connected
                    </Text>
                    <div style={{ fontSize: "1.15rem", fontWeight: 700, color: "#10b981" }}>{todayCallMetrics.connected}</div>
                  </div>
                </div>

                {/* Status Cluster Filter Chips */}
                {todayCallMetrics.statusCluster.length > 0 && (
                  <div className="today-status-cluster">
                    <button type="button" className={`history-status-chip ${statusFilter === "All" ? "active" : ""}`} onClick={() => setStatusFilter("All")}>
                      <span>All</span>
                      <strong>{todayCallMetrics.total}</strong>
                    </button>
                    {todayCallMetrics.statusCluster.map((item) => (
                      <button key={item.status} type="button" className={`history-status-chip ${statusFilter === item.status ? "active" : ""}`} onClick={() => setStatusFilter(statusFilter === item.status ? "All" : item.status)}>
                        <span>{item.status}</span>
                        <strong>{item.count}</strong>
                      </button>
                    ))}
                  </div>
                )}
              </Flex>
            </Flex>
          </div>
        )}

        {/* ── 3. MASTER-DETAIL TWO-COLUMN FRAME ── */}
        <div className="lead-split-frame">
          {/* LEFT RAIL: Companies List */}
          <div className="company-rail-pane">
            <Flex vertical style={{ height: "100%" }}>
              {/* Header */}
              <Flex justify="space-between" align="center" className="company-rail-header">
                <Text strong style={{ fontSize: "0.95rem" }}>
                  {activeTab === "our_clients" || activeTab === "clients" || activeTab === "crm_clients" || activeTab === "crm_client_onboard_requests" ? "Clients" : "Companies"}
                </Text>
                <Tag color="blue" style={{ borderRadius: 10 }}>
                  {totalCompanies || filteredCompanies.length} accounts
                </Tag>
              </Flex>

              {/* Scrollable List with Infinite Scroll */}
              <div className="company-rail-list hide-scrollbar" onScroll={handleRailScroll}>
                {filteredCompanies.length > 0 ? (
                  <>
                    {filteredCompanies.map((comp) => {
                      const isSelected = comp.id === selectedCompanyId;
                      const contactCount = (companyDetailsCache[comp.id]?.contacts?.length ?? companyDetailsCache[comp.name]?.contacts?.length) ?? comp.contacts?.length ?? comp.directorCount ?? comp.count ?? 0;

                      return (
                        <button type="button" key={comp.id} className={`company-rail-item ${isSelected ? "active" : ""}`} onClick={() => handleSelectCompany(comp)} style={{ width: "100%", textAlign: "left", display: "block", fontFamily: "inherit" }}>
                          <Flex justify="space-between" align="center">
                            <Flex vertical style={{ maxWidth: "80%" }}>
                              <Text strong style={{ fontSize: "0.9rem", color: isSelected ? "#2459d3" : "#0f172a" }}>
                                {comp.name}
                              </Text>
                              <Text type="secondary" style={{ fontSize: "0.75rem" }} ellipsis>
                                {comp.division || comp.category}
                              </Text>
                            </Flex>
                            <span className={`company-count-badge ${isSelected ? "active" : ""}`}>
                              {contactCount}
                            </span>
                          </Flex>
                        </button>
                      );
                    })}

                    {loadingMore && (
                      <div className="company-rail-loading-more">
                        <Spin size="small" />
                        <Text type="secondary" style={{ fontSize: "0.75rem" }}>
                          Loading more...
                        </Text>
                      </div>
                    )}
                  </>
                ) : (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No accounts found" />
                )}
              </div>
            </Flex>
          </div>

          {/* RIGHT PANE: Company Contacts & Resizable Table */}
          <div className="leads-content-pane">
            <Spin spinning={loadingDetails} tip="Loading company details...">
              <Flex vertical gap={16}>
                {/* Header */}
                {activeCompany && (
                  <Flex justify="space-between" align="center" wrap="wrap" gap={12} className="leads-content-header">
                    <Flex vertical>
                      <Title level={4} style={{ margin: 0 }}>
                        {activeCompany.name}
                      </Title>
                      <Text type="secondary" style={{ fontSize: "0.8rem" }}>
                        {activeCompany.division || activeCompany.category} &bull; {activeCompany.city || "India"}
                      </Text>
                    </Flex>

                    <Space>
                      <Button type="primary" icon={<ThunderboltOutlined />} onClick={() => setIsAiSummaryModalOpen(true)} style={{ background: "linear-gradient(135deg, #2459d3, #7c3aed)", border: "none" }}>
                        AI Summary
                      </Button>
                      <Button icon={<FullscreenOutlined />} onClick={() => setIsFullViewOpen(true)}>
                        Full View
                      </Button>
                    </Space>
                  </Flex>
                )}

                {/* View Rendering: Table vs Grid */}
                <LeadContactsView
                  activeContacts={activeContacts}
                  viewMode={viewMode}
                  tableColumns={tableColumns}
                  totalTableWidth={totalTableWidth}
                  activeCompany={activeCompany}
                  handleUpdateLeadStatus={handleUpdateLeadStatus}
                  handleAddRemark={handleAddRemark}
                  handleToggleStar={handleToggleStar}
                  handleToggleFavourite={handleToggleFavourite}
                  setDetailDrawerLead={setDetailDrawerLead}
                  handleScheduleFollowup={handleScheduleFollowup}
                  availableCallStatuses={availableCallStatuses}
                  getAvailableStatusesForLead={getAvailableStatusesForLead}
                  handleOpenMail={handleOpenMail}
                  loadingDetails={loadingDetails}
                  onOpenQuotation={(lead) => {
                    setSelectedLeadForAction({
                      _id: lead._id,
                      leadId: lead._id,
                      leadCompanyName: activeCompany?.name || lead.leadCompanyName || "",
                      contactName: lead.contactName,
                      contactNumber: lead.contactNumber,
                      directorEmailAddress: lead.directorEmailAddress,
                    });
                    setQuotationModalOpen(true);
                  }}
                  onOpenProposal={(lead) => {
                    setSelectedLeadForAction({
                      _id: lead._id,
                      leadId: lead._id,
                      leadCompanyName: activeCompany?.name || lead.leadCompanyName || "",
                      contactName: lead.contactName,
                      contactNumber: lead.contactNumber,
                      directorEmailAddress: lead.directorEmailAddress,
                    });
                    setProposalModalOpen(true);
                  }}
                />
              </Flex>
            </Spin>
          </div>
        </div>

        {/* ── 4. POPUPS, MODALS & DRAWERS ── */}
        <AiSummaryModal visible={isAiSummaryModalOpen} company={activeCompany} onClose={() => setIsAiSummaryModalOpen(false)} />

        <AddLeadModal visible={isAddLeadModalOpen} onClose={() => setIsAddLeadModalOpen(false)} onAddLead={handleAddLead} onImportLeads={handleImportLeads} companies={companies} availableStatuses={availableCallStatuses} companyCode={effectiveCompanyCode} employeeId={effectiveEmployeeId} employee={employee} />

        <LeadDetailDrawer
          visible={Boolean(detailDrawerLead)}
          lead={currentDetailLead}
          company={activeCompany}
          onClose={() => setDetailDrawerLead(null)}
          onAddRemark={handleAddRemark}
          onStatusChange={handleUpdateLeadStatus}
          onToggleStar={handleToggleStar}
          onToggleFavourite={handleToggleFavourite}
          availableStatuses={availableCallStatuses}
          getAvailableStatusesForLead={getAvailableStatusesForLead}
          onOpenMail={handleOpenMail}
        />

        {/* Schedule Activity / Follow-up Modal */}
        <ScheduleActivityModal
          open={activityModalOpen}
          onCancel={() => {
            setActivityModalOpen(false);
            setSelectedLeadForActivity(null);
          }}
          onSuccess={async () => {
            if (selectedLeadForActivity?._id) {
              await persistLeadStatusUpdate(selectedLeadForActivity._id, "Follow Up");
            }
            if (activeCompany?.name) {
              fetchCompanyDetails(activeCompany.name);
            }
            setActivityModalOpen(false);
            setSelectedLeadForActivity(null);
          }}
          lead={selectedLeadForActivity}
          companyName={activeCompany?.name || selectedLeadForActivity?.companyName || ""}
          defaultType={activityDefaultType}
        />

        {/* Company Full View 360-degree modal */}
        <CompanyFullViewModal visible={isFullViewOpen} company={activeCompany} lead={activeCompany?.leads?.[0] || activeCompany?.contacts?.[0]} role={effectiveRole} onClose={() => setIsFullViewOpen(false)} />

        {/* Quick Access Create Quotation / Proposal Modals */}
        <QuotationModal
          open={quotationModalOpen}
          visible={quotationModalOpen}
          onClose={() => {
            setQuotationModalOpen(false);
            setSelectedLeadForAction(null);
          }}
          prefillLead={selectedLeadForAction}
          companyCode={effectiveCompanyCode}
          employeeId={effectiveEmployeeId}
          onSaveQuotation={() => {
            setCompanyDetailsCache({});
            loadInitialData();
          }}
        />

        <ProposalModal
          open={proposalModalOpen}
          visible={proposalModalOpen}
          onClose={() => {
            setProposalModalOpen(false);
            setSelectedLeadForAction(null);
          }}
          prefillLead={selectedLeadForAction}
          companyCode={effectiveCompanyCode}
          employeeId={effectiveEmployeeId}
          onSaveProposal={() => {
            setCompanyDetailsCache({});
            loadInitialData();
          }}
        />

        <AddDealModal
          visible={dealModalOpen}
          onClose={() => {
            setDealModalOpen(false);
            setSelectedLeadForDeal(null);
          }}
          onSaveDeal={handleSaveNewDeal}
          prefillLead={selectedLeadForDeal}
          isEmployee={effectiveRole === "employee"}
          defaultEmployee={{ id: effectiveEmployeeId, name: effectiveEmployeeName }}
        />

        {/* Mail Compose Drawer */}
        <MailComposeDrawer
          open={mailDrawerOpen}
          visible={mailDrawerOpen}
          onClose={() => {
            setMailDrawerOpen(false);
            setMailLead(null);
          }}
          lead={mailLead}
          companyCode={effectiveCompanyCode}
          employeeInfo={{ id: effectiveEmployeeId, name: effectiveEmployeeName }}
          onSuccess={() => {
            if (activeCompany?.name) {
              fetchCompanyDetails(activeCompany.name);
            }
          }}
        />
      </Flex>
    </Spin>
  );
}

export default LeadsTemplate;
